import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { format } from "date-fns";
import { User, LogOut, X } from "lucide-react";
import { useAuth } from "@/features/auth/AuthProvider";
import { useMe } from "@vaulzo/core/hooks/useMe";
import ProfileModal from "./ProfileModal";

const navItems = [
  { value: "overview", label: "Net worth" },
  { value: "assets", label: "Assets" },
  { value: "liabilities", label: "Liabilities" },
  { value: "expenses", label: "Expenses" },
];

// Display name and initials from the profile, falling back to the email
// prefix ("sameer.choubey" → "Sameer Choubey"-ish) when names aren't set.
function displayIdentity(me) {
  const first = me?.first_name?.trim();
  const last = me?.last_name?.trim();
  if (first || last) {
    const name = [first, last].filter(Boolean).join(" ");
    const initials = ((first?.[0] || "") + (last?.[0] || first?.[1] || "")).toUpperCase();
    return { name, initials };
  }
  const email = me?.email;
  if (!email) return { name: "Your account", initials: "ME" };
  const namePart = email.split("@")[0].replace(/[._-]+/g, " ").trim();
  const words = namePart.split(/\s+/).filter(Boolean);
  const initials =
    words.length >= 2 ? (words[0][0] + words[1][0]).toUpperCase() : namePart.slice(0, 2).toUpperCase();
  return { name: namePart, initials };
}

// Premium isn't ready — keep the banner code but don't render it for now.
const SHOW_PROMO_BANNER = false;

/**
 * Floating "cream glass" capsule: brand, the section switch with a pill
 * that slides between sections, a price-freshness note and the account
 * menu. Sticky, so content scrolls beneath it.
 */
export default function Header({ activeTab, onTabChange, onUpgrade, pricesRefreshing = false, updatedAt = null }) {
  const { logout } = useAuth();
  // Once dismissed, the promo banner stays hidden for this browser.
  const [bannerOpen, setBannerOpen] = useState(
    () => localStorage.getItem("premiumBannerDismissed") !== "1"
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const menuRef = useRef(null);
  const navRef = useRef(null);
  // The sliding pill is measured from the active button, so it follows
  // any label width and survives resizes.
  const [pill, setPill] = useState(null);

  const dismissBanner = () => {
    setBannerOpen(false);
    localStorage.setItem("premiumBannerDismissed", "1");
  };

  const { data: me } = useMe();
  const { name: displayName, initials } = displayIdentity(me);
  const email = me?.email;

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  useLayoutEffect(() => {
    const el = navRef.current?.querySelector(`[data-tab="${activeTab}"]`);
    const measure = () => {
      if (el) setPill({ left: el.offsetLeft, width: el.offsetWidth });
    };
    measure();
    window.addEventListener("resize", measure);
    // On a fresh load the first measure happens before the web font has
    // arrived, with the fallback font's wider metrics; the pill then hugged
    // the wrong width until the next tab change. Measure again when the
    // fonts land, and whenever the active button's own size changes.
    let cancelled = false;
    document.fonts?.ready?.then(() => !cancelled && measure());
    const ro = typeof ResizeObserver !== "undefined" && el ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [activeTab]);

  const status = pricesRefreshing
    ? "Updating prices…"
    : updatedAt
      ? `Prices as of ${format(new Date(updatedAt), "HH:mm")}`
      : "";

  return (
    <>
      {/* Promo banner */}
      {SHOW_PROMO_BANNER && bannerOpen && (
        <div className="w-full bg-vaulzo-accent text-vaulzo-on-fill animate-vz-fade">
          <div className="mx-auto flex max-w-[800px] items-center gap-3 px-4 py-[10px] sm:gap-[14px] sm:px-6">
            {/* The pill is decoration — give phones the space back */}
            <span className="hidden whitespace-nowrap rounded-full bg-vaulzo-surface/20 px-[9px] py-[3px] text-[11px] font-bold uppercase tracking-[0.06em] sm:inline">
              50% off
            </span>
            <span className="flex-1 text-[13.5px] leading-tight">
              Go Premium and <strong className="font-semibold">auto-sync your net worth</strong> across every account — half price this month.
            </span>
            <button
              onClick={onUpgrade}
              className="flex-none whitespace-nowrap rounded-lg bg-vaulzo-surface px-[15px] py-[7px] text-[13px] font-semibold text-vaulzo-accent transition-opacity hover:opacity-90"
            >
              Upgrade
            </button>
            <button
              onClick={dismissBanner}
              title="Dismiss"
              aria-label="Dismiss"
              className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-full bg-vaulzo-surface/[0.14] text-vaulzo-on-fill transition-opacity hover:opacity-60"
            >
              <X className="h-[13px] w-[13px]" strokeWidth={2.4} />
            </button>
          </div>
        </div>
      )}

      {/* Floating capsule: 12px of air above it even when stuck */}
      <header className="sticky top-0 z-20 px-3 pt-3 sm:px-4">
        <div className="mx-auto flex h-14 max-w-[800px] items-center gap-2 rounded-full border border-[rgb(var(--vz-ink)/0.08)] bg-[linear-gradient(180deg,rgb(var(--vz-surface)/0.9),rgb(var(--vz-surface)/0.7))] pl-2 pr-[11px] shadow-[0_12px_32px_rgba(23,24,26,0.10),0_1px_2px_rgba(23,24,26,0.06)] backdrop-blur-[18px] backdrop-saturate-[140%] sm:gap-3 sm:pl-[18px]">
          {/* Brand → overview (hidden on phones: four section tabs need the room) */}
          <button
            type="button"
            onClick={() => onTabChange("overview")}
            className="hidden min-w-0 flex-1 items-center gap-[10px] text-left sm:flex"
          >
            <span className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-lg bg-vaulzo-ink">
              <span className="h-2 w-2 rounded-full border-2 border-vaulzo-bg" />
            </span>
            <span className="hidden text-[15px] font-semibold tracking-[-0.01em] text-vaulzo-ink sm:block">Vaulzo</span>
          </button>

          {/* Section switch */}
          <nav ref={navRef} aria-label="Sections" className="relative flex flex-1 items-center gap-[2px] sm:flex-none">
            {pill && (
              <span
                aria-hidden
                className="absolute inset-y-0 rounded-full bg-vaulzo-ink shadow-[0_4px_12px_rgba(23,24,26,0.25)] transition-[left,width] duration-300 ease-[cubic-bezier(.2,.8,.2,1)]"
                style={{ left: pill.left, width: pill.width }}
              />
            )}
            {navItems.map((item) => {
              const active = activeTab === item.value;
              return (
                <button
                  key={item.value}
                  type="button"
                  data-tab={item.value}
                  aria-current={active ? "page" : undefined}
                  onClick={() => onTabChange(item.value)}
                  className={`relative z-[1] whitespace-nowrap rounded-full px-2 py-[7px] text-[12px] transition-colors duration-200 sm:px-[14px] sm:text-[13px] ${
                    active ? "font-semibold text-vaulzo-on-fill" : "font-medium text-vaulzo-faint hover:text-vaulzo-ink"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* Price freshness + account */}
          <div className="relative flex flex-1 items-center justify-end gap-3" ref={menuRef}>
            {status && (
              <span
                title="Prices refresh when you open the app"
                className="hidden items-center gap-[6px] whitespace-nowrap text-[12px] text-vaulzo-faint md:flex"
              >
                <span className={`h-[6px] w-[6px] rounded-full bg-vaulzo-green ${pricesRefreshing ? "animate-pulse" : ""}`} />
                {status}
              </span>
            )}
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              title="Account"
              className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-full bg-vaulzo-accent text-[12px] font-bold tracking-[0.04em] text-vaulzo-on-fill shadow-[0_0_0_2px_rgba(91,107,140,0.18)] transition-shadow hover:shadow-[0_0_0_3px_rgba(91,107,140,0.35)]"
            >
              {initials}
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-[50px] z-40 w-[220px] rounded-[18px] border border-[rgb(var(--vz-ink)/0.08)] bg-vaulzo-surface/[0.96] p-[6px] shadow-[0_16px_40px_rgba(23,24,26,0.18)] backdrop-blur-[18px] animate-vz-fade">
                <div className="flex items-center gap-[10px] px-3 pb-3 pt-[10px]">
                  <div className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-full bg-vaulzo-accent text-[12px] font-semibold text-vaulzo-on-fill">
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold capitalize text-vaulzo-ink">
                      {displayName}
                    </div>
                    <div className="truncate text-[12px] text-vaulzo-fainter">
                      {email || "Signed in"}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    setProfileOpen(true);
                  }}
                  className="flex w-full items-center gap-[10px] rounded-xl px-3 py-[10px] text-left text-sm text-vaulzo-menu transition-colors hover:bg-vaulzo-row"
                >
                  <User className="h-4 w-4" strokeWidth={1.7} />
                  Profile
                </button>
                <div className="mx-2 my-[5px] h-px bg-vaulzo-hairline" />
                <button
                  onClick={logout}
                  className="flex w-full items-center gap-[10px] rounded-xl px-3 py-[10px] text-left text-sm font-semibold text-vaulzo-close-red transition-opacity hover:opacity-60"
                >
                  <LogOut className="h-4 w-4" strokeWidth={1.7} />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
    </>
  );
}
